import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { ArrowLeft, MapPin, Plus, Minus, Users, Save, Printer, X, FileText, ChevronDown, ChevronUp, Download, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TripAccountabilityForm({ user, onBack }) {
  const groupCode = user?.group_code || "";
  const groupName = user?.group_name || "";
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState("new");
  const [pastForms, setPastForms] = useState([]);
  const [pastLoading, setPastLoading] = useState(false);
  const [expandedForm, setExpandedForm] = useState(null);
  const [campers, setCampers] = useState([]);
  const [tripName, setTripName] = useState("");
  const [sections, setSections] = useState([
    { counselor: "", campers: [] },
    { counselor: "", campers: [] },
  ]);
  const [saving, setSaving] = useState(false);
  const [selectedCampers, setSelectedCampers] = useState([]);
  const [lateNames, setLateNames] = useState({});
  const [addingLate, setAddingLate] = useState(null);

  const handleAddLateCamper = async (formId, sectionIdx) => {
    const key = `${formId}-${sectionIdx}`;
    const name = (lateNames[key] || "").trim();
    if (!name) { toast.error("Enter the camper's name"); return; }
    setAddingLate(key);
    try {
      const res = await axios.patch(`${API}/trip-forms/${formId}/add-camper`, { camper_name: name, section_index: sectionIdx }, { headers });
      setPastForms((prev) => prev.map((f) => f.id === formId ? { ...f, counselor_sections: res.data.counselor_sections } : f));
      setLateNames((prev) => ({ ...prev, [key]: "" }));
      toast.success(`${name} added to the form`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add camper");
    } finally {
      setAddingLate(null);
    }
  };

  // Touch drag state
  const [touchDragging, setTouchDragging] = useState(null);
  const touchCloneRef = useRef(null);
  const touchTimerRef = useRef(null);
  const touchStartPos = useRef(null);

  const today = new Date().toLocaleDateString("en-US", { timeZone: "America/New_York", month: "2-digit", day: "2-digit", year: "numeric" });

  useEffect(() => {
    const fetchCampers = async () => {
      try {
        const res = await axios.get(`${API}/campers`, { headers });
        setCampers(res.data || []);
      } catch (e) {
        console.error("Failed to fetch campers", e);
      }
    };
    fetchCampers();
  }, []);

  const fetchPastForms = async () => {
    setPastLoading(true);
    try {
      const res = await axios.get(`${API}/trip-forms?group_code=${groupCode}`, { headers });
      setPastForms(res.data || []);
    } catch { setPastForms([]); }
    setPastLoading(false);
  };

  useEffect(() => { fetchPastForms(); }, [groupCode]);

  const getDisplayName = (c) => c.first_name + " " + c.last_name;

  // Collect all placed campers
  const placedCampers = new Set();
  sections.forEach((s) => s.campers.forEach((name) => { if (name) placedCampers.add(name); }));

  const unplacedCampers = campers
    .filter((c) => !placedCampers.has(getDisplayName(c)))
    .sort((a, b) => a.last_name.localeCompare(b.last_name));

  // Place campers into section (supports multi-select)
  const placeCampersInSection = (sectionIdx, names) => {
    const fresh = (names || []).filter((n) => n && !placedCampers.has(n));
    if (fresh.length === 0) return;
    setSections((prev) =>
      prev.map((s, i) => i === sectionIdx ? { ...s, campers: [...s.campers, ...fresh] } : s)
    );
    setSelectedCampers([]);
  };

  // Remove camper from section
  const removeCamperFromSection = (sectionIdx, camperIdx) => {
    setSections((prev) =>
      prev.map((s, i) =>
        i === sectionIdx ? { ...s, campers: s.campers.filter((_, ci) => ci !== camperIdx) } : s
      )
    );
  };

  // Update counselor name
  const updateCounselor = (sectionIdx, name) => {
    setSections((prev) =>
      prev.map((s, i) => i === sectionIdx ? { ...s, counselor: name } : s)
    );
  };

  // Add / remove sections
  const addSection = () => setSections((prev) => [...prev, { counselor: "", campers: [] }]);
  const removeSection = (idx) => setSections((prev) => prev.filter((_, i) => i !== idx));

  // Tap-to-select (multi): tap campers to build a selection, then tap a section
  const handleCamperTap = (name) => {
    if (touchDragging) return;
    setSelectedCampers((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const handleSectionTap = (sectionIdx) => {
    if (selectedCampers.length > 0) {
      placeCampersInSection(sectionIdx, selectedCampers);
    }
  };

  // Desktop HTML5 Drag
  const handleDragStart = (e, name) => {
    e.dataTransfer.setData("text/plain", name);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = (e, sectionIdx) => {
    e.preventDefault();
    const name = e.dataTransfer.getData("text/plain");
    placeCampersInSection(sectionIdx, [name]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  // Touch Drag (mobile)
  const handleTouchStart = (e, name) => {
    if (placedCampers.has(name)) return;
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };
    touchTimerRef.current = setTimeout(() => {
      setTouchDragging(name);
      setSelectedCampers([]);
      const clone = document.createElement("div");
      clone.className = "fixed z-[999] pointer-events-none px-3 py-2 rounded-lg bg-teal-600 text-white text-xs font-bold shadow-xl opacity-90";
      clone.textContent = name;
      clone.style.left = `${touch.clientX - 40}px`;
      clone.style.top = `${touch.clientY - 20}px`;
      document.body.appendChild(clone);
      touchCloneRef.current = clone;
    }, 400);
  };

  const handleTouchMoveCard = (e) => {
    if (touchTimerRef.current && !touchDragging) {
      const touch = e.touches[0];
      const dx = Math.abs(touch.clientX - (touchStartPos.current?.x || 0));
      const dy = Math.abs(touch.clientY - (touchStartPos.current?.y || 0));
      if (dx > 10 || dy > 10) {
        clearTimeout(touchTimerRef.current);
        touchTimerRef.current = null;
      }
    }
  };

  const handleTouchEndCard = () => {
    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
  };

  const handleGlobalTouchMove = useCallback((e) => {
    if (!touchDragging || !touchCloneRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    touchCloneRef.current.style.left = `${touch.clientX - 40}px`;
    touchCloneRef.current.style.top = `${touch.clientY - 20}px`;
    document.querySelectorAll("[data-trip-drop]").forEach((el) => el.classList.remove("ring-2", "ring-teal-500", "bg-teal-50"));
    const target = document.elementFromPoint(touch.clientX, touch.clientY);
    if (target) {
      const zone = target.closest("[data-trip-drop]");
      if (zone) zone.classList.add("ring-2", "ring-teal-500", "bg-teal-50");
    }
    const scrollMargin = 60;
    if (touch.clientY < scrollMargin) window.scrollBy(0, -8);
    else if (touch.clientY > window.innerHeight - scrollMargin) window.scrollBy(0, 8);
  }, [touchDragging]);

  const handleGlobalTouchEnd = useCallback((e) => {
    if (!touchDragging) return;
    const touch = e.changedTouches[0];
    document.querySelectorAll("[data-trip-drop]").forEach((el) => el.classList.remove("ring-2", "ring-teal-500", "bg-teal-50"));
    const target = document.elementFromPoint(touch.clientX, touch.clientY);
    if (target) {
      const zone = target.closest("[data-trip-drop]");
      if (zone) {
        const si = parseInt(zone.dataset.sectionIdx);
        if (!isNaN(si)) placeCampersInSection(si, [touchDragging]);
      }
    }
    if (touchCloneRef.current) {
      document.body.removeChild(touchCloneRef.current);
      touchCloneRef.current = null;
    }
    setTouchDragging(null);
  }, [touchDragging, placedCampers]);

  useEffect(() => {
    if (touchDragging) {
      document.addEventListener("touchmove", handleGlobalTouchMove, { passive: false });
      document.addEventListener("touchend", handleGlobalTouchEnd);
      return () => {
        document.removeEventListener("touchmove", handleGlobalTouchMove);
        document.removeEventListener("touchend", handleGlobalTouchEnd);
      };
    }
  }, [touchDragging, handleGlobalTouchMove, handleGlobalTouchEnd]);

  const handleSave = async () => {
    if (!tripName.trim()) {
      toast.error("Please enter the trip name");
      return;
    }
    const validSections = sections
      .filter((s) => s.counselor.trim())
      .map((s) => ({ counselor: s.counselor.trim(), campers: s.campers.filter((c) => c) }));
    if (validSections.length === 0) {
      toast.error("Please add at least one counselor name");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/trip-forms`, { trip_name: tripName, counselor_sections: validSections }, { headers });
      toast.success("Trip form saved!");
      onBack();
    } catch (err) {
      toast.error("Failed to save trip form");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-slate-100" data-testid="trip-form-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-teal-700 text-white px-3 py-2 shadow-lg" data-testid="trip-form-header">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onBack} className="text-white hover:bg-teal-600 h-8 px-2 gap-1" data-testid="trip-back-btn">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs">Dashboard</span>
            </Button>
            <MapPin className="w-5 h-5" />
            <div>
              <h1 className="font-bold text-sm">Trip Accountability Form</h1>
              <p className="text-[10px] text-teal-200">{groupCode} — {groupName} | {today}</p>
            </div>
          </div>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-white text-teal-700 hover:bg-teal-50 font-bold text-xs h-8" data-testid="trip-save-btn">
            <Save className="w-3.5 h-3.5 mr-1" />{saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 py-3 space-y-3">
        {/* Tabs */}
        <div className="flex gap-2" data-testid="trip-tabs">
          <button onClick={() => setTab("new")} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${tab === "new" ? "bg-teal-600 text-white" : "bg-white text-gray-600 hover:bg-gray-100 border"}`} data-testid="trip-tab-new">
            New Form
          </button>
          <button onClick={() => { setTab("past"); fetchPastForms(); }} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-1.5 ${tab === "past" ? "bg-teal-600 text-white" : "bg-white text-gray-600 hover:bg-gray-100 border"}`} data-testid="trip-tab-past">
            <FileText className="w-3.5 h-3.5" /> Past Forms {pastForms.length > 0 && <Badge className="bg-teal-100 text-teal-800 text-[10px] px-1.5">{pastForms.length}</Badge>}
          </button>
        </div>

        {/* Past Forms Tab */}
        {tab === "past" && (
          <div className="space-y-3" data-testid="trip-past-forms">
            {pastLoading && <p className="text-sm text-muted-foreground text-center py-8">Loading forms...</p>}
            {!pastLoading && pastForms.length === 0 && (
              <div className="text-center py-12">
                <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No trip forms filed yet for {groupCode}.</p>
              </div>
            )}
            {!pastLoading && pastForms.map(f => {
              const isExpanded = expandedForm === f.id;
              return (
                <Card key={f.id} className="cursor-pointer transition-shadow hover:shadow-md border-teal-200" data-testid={`trip-card-${f.id}`}>
                  <CardContent className="p-0">
                    <button onClick={() => setExpandedForm(isExpanded ? null : f.id)} className="w-full text-left p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-teal-100 text-teal-800 text-[10px]">{f.group_code}</Badge>
                            <span className="text-xs text-muted-foreground">{f.date}</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">{f.trip_name || "Untitled Trip"}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{(f.counselor_sections || []).reduce((sum, s) => sum + (s.campers?.length || 0), 0)} campers assigned</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const res = await axios.get(`${API}/trip-forms/${f.id}/pdf`, { headers, responseType: "blob" });
                                window.open(window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
                              } catch { toast.error("Failed to load PDF"); }
                            }}
                            className="text-gray-400 hover:text-gray-600"
                            data-testid={`trip-pdf-${f.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </div>
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="border-t px-4 pb-4 pt-3 space-y-3 bg-gray-50/50">
                        {(f.counselor_sections || []).map((s, i) => {
                          const key = `${f.id}-${i}`;
                          const onFormNames = new Set((f.counselor_sections || []).flatMap((sec) => sec.campers || []));
                          const suggestions = campers.map(getDisplayName).filter((n) => !onFormNames.has(n));
                          return (
                          <div key={i} className="bg-white rounded-lg p-3 border">
                            <p className="text-xs font-bold text-gray-600 mb-1">Group {i + 1}: {s.counselor || "No counselor"}</p>
                            <div className="flex flex-wrap gap-1">
                              {(s.campers || []).map((c, j) => <Badge key={j} variant="outline" className="text-[10px]">{c}</Badge>)}
                              {(!s.campers || s.campers.length === 0) && <span className="text-xs text-gray-400">No campers</span>}
                            </div>
                            <div className="flex items-center gap-1.5 mt-2">
                              <Input
                                placeholder="Add late camper..."
                                value={lateNames[key] || ""}
                                onChange={(e) => setLateNames((prev) => ({ ...prev, [key]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === "Enter") handleAddLateCamper(f.id, i); }}
                                list={`late-suggest-${key}`}
                                className="h-7 text-xs border-teal-200"
                                data-testid={`trip-late-input-${f.id}-${i}`}
                              />
                              <datalist id={`late-suggest-${key}`}>
                                {suggestions.map((n) => <option key={n} value={n} />)}
                              </datalist>
                              <Button
                                size="sm"
                                onClick={() => handleAddLateCamper(f.id, i)}
                                disabled={addingLate === key}
                                className="h-7 px-2 bg-teal-600 hover:bg-teal-700 text-white text-xs shrink-0"
                                data-testid={`trip-late-add-btn-${f.id}-${i}`}
                              >
                                <Plus className="w-3 h-3 mr-0.5" /> Add
                              </Button>
                            </div>
                          </div>
                          );
                        })}
                        <div className="flex items-center gap-1 text-[10px] text-gray-400 pt-1">
                          <Clock className="w-3 h-3" /> Submitted {f.submitted_at ? new Date(f.submitted_at).toLocaleString() : f.date}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* New Form Tab */}
        {tab === "new" && (
        <div className="space-y-3">
        {/* Trip Name Input */}
        <Card className="bg-white/90 border-teal-300">
          <CardContent className="p-3">
            <label className="text-[10px] font-semibold text-teal-800 uppercase tracking-wider">Trip Name / Destination *</label>
            <Input
              placeholder="Enter trip name..."
              value={tripName}
              onChange={(e) => setTripName(e.target.value)}
              className="mt-1 border-teal-200 focus:ring-teal-400 text-sm"
              data-testid="trip-name-input"
            />
          </CardContent>
        </Card>

        {/* Instructions */}
        <p className="text-center text-[10px] sm:text-xs text-teal-700 italic px-1">
          Tap one or more campers to select them, then tap a counselor section to place them all. Hold to drag on mobile. Drag &amp; drop on desktop.
        </p>

        {/* Camper Pool */}
        <Card className="bg-white/90 border-teal-300">
          <CardHeader className="py-2 px-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-xs font-bold text-teal-800">
                <Users className="w-3.5 h-3.5 inline mr-1" />
                Camper Pool — {unplacedCampers.length} of {campers.length} remaining
              </CardTitle>
              <div className="flex items-center gap-1.5">
                {selectedCampers.length > 0 && (
                  <>
                    <Badge className="bg-teal-600 text-white text-[10px]" data-testid="trip-selected-count">
                      {selectedCampers.length} selected
                    </Badge>
                    <button
                      onClick={() => setSelectedCampers([])}
                      className="text-[10px] font-semibold text-red-500 hover:text-red-700 px-1.5 py-0.5 rounded border border-red-200 bg-white"
                      data-testid="trip-clear-selection"
                    >
                      Clear
                    </button>
                  </>
                )}
                {unplacedCampers.length > 0 && (
                  <button
                    onClick={() => setSelectedCampers(unplacedCampers.map(getDisplayName))}
                    className="text-[10px] font-semibold text-teal-700 hover:text-teal-900 px-1.5 py-0.5 rounded border border-teal-300 bg-white"
                    data-testid="trip-select-all"
                  >
                    Select All
                  </button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-2.5 pt-0">
            <div className="max-h-[40vh] overflow-y-auto overscroll-contain pr-1" data-testid="trip-pool-scroll">
              <div className="flex flex-wrap gap-1.5" data-testid="trip-camper-pool">
                {unplacedCampers.map((c) => {
                  const name = getDisplayName(c);
                  const isSelected = selectedCampers.includes(name);
                  const isDragging = touchDragging === name;
                  return (
                    <div
                      key={c.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, name)}
                      onTouchStart={(e) => handleTouchStart(e, name)}
                      onTouchMove={handleTouchMoveCard}
                      onTouchEnd={handleTouchEndCard}
                      onClick={() => handleCamperTap(name)}
                      className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border text-[11px] sm:text-xs font-medium transition-all select-none ${
                        isDragging
                          ? "opacity-30 scale-95"
                          : isSelected
                          ? "bg-teal-500 text-white border-teal-600 ring-2 ring-teal-300 shadow-lg scale-105"
                          : "bg-white border-teal-200 hover:border-teal-400 hover:shadow cursor-grab active:cursor-grabbing"
                      }`}
                      data-testid={`trip-pool-camper-${c.id}`}
                    >
                      {name}
                    </div>
                  );
                })}
                {unplacedCampers.length === 0 && (
                  <p className="text-xs text-muted-foreground italic py-2 w-full text-center">All campers assigned</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Counselor Sections */}
        <div className="space-y-3">
          {sections.map((section, sIdx) => (
            <Card
              key={sIdx}
              className={`bg-white/90 border-teal-300 transition-all ${selectedCampers.length > 0 ? "cursor-pointer hover:ring-2 hover:ring-teal-400" : ""}`}
              data-trip-drop=""
              data-section-idx={sIdx}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, sIdx)}
              onClick={() => handleSectionTap(sIdx)}
              data-testid={`trip-section-${sIdx}`}
            >
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1">
                    <Badge variant="secondary" className="bg-teal-100 text-teal-800 text-xs font-bold shrink-0">
                      {sIdx + 1}
                    </Badge>
                    <Input
                      placeholder="Counselor name..."
                      value={section.counselor}
                      onChange={(e) => updateCounselor(sIdx, e.target.value)}
                      className="text-sm h-8 border-teal-200"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`trip-counselor-${sIdx}`}
                    />
                  </div>
                  {sections.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); removeSection(sIdx); }}
                      className="text-red-400 hover:text-red-600 h-8 w-8 p-0 shrink-0"
                      data-testid={`trip-remove-section-${sIdx}`}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Assigned campers */}
                <div className="min-h-[40px] rounded-lg border-2 border-dashed border-teal-200 p-2 flex flex-wrap gap-1.5" data-testid={`trip-section-campers-${sIdx}`}>
                  {section.campers.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic w-full text-center py-1">
                      {selectedCampers.length > 0
                        ? `Tap here to place ${selectedCampers.length} camper${selectedCampers.length !== 1 ? "s" : ""}`
                        : "Drop or tap campers here"}
                    </p>
                  ) : (
                    section.campers.map((name, cIdx) => (
                      <div
                        key={`${name}-${cIdx}`}
                        className="flex items-center gap-1 px-2 py-1 rounded-md bg-teal-50 border border-teal-200 text-xs font-medium text-teal-800"
                        data-testid={`trip-assigned-${sIdx}-${cIdx}`}
                      >
                        <span>{name}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeCamperFromSection(sIdx, cIdx); }}
                          className="ml-0.5 text-teal-400 hover:text-red-500 transition-colors"
                          data-testid={`trip-remove-camper-${sIdx}-${cIdx}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground text-right">{section.campers.length} camper{section.campers.length !== 1 ? "s" : ""}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Add Section */}
        <Button variant="outline" size="sm" onClick={addSection} className="w-full border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="trip-add-section">
          <Plus className="w-4 h-4 mr-1" /> Add Counselor Group
        </Button>

        {/* Bottom Save */}
        <div className="pb-4">
          <Button onClick={() => { handleSave(); fetchPastForms(); }} disabled={saving} className="w-full bg-teal-600 hover:bg-teal-700 font-bold" data-testid="trip-save-bottom-btn">
            <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Trip Form"}
          </Button>
        </div>
        </div>
        )}
      </div>
    </div>
  );
}

